import React, { useEffect, useRef } from 'react';
import './HomeMotion.css';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const HomeMotion = ({ children }) => {
  const pageRef = useRef(null);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    const sections = Array.from(page.querySelectorAll('[data-home-motion-section]'));
    const reduceMotion = typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mobileViewport = typeof window.matchMedia === 'function'
      && window.matchMedia('(max-width: 767px)').matches;
    let frameId = null;

    const updateScrollEffects = () => {
      frameId = null;

      const pageTop = page.offsetTop;
      const scrollDistance = clamp(window.scrollY - pageTop, 0, Math.max(page.offsetHeight - window.innerHeight, 1));
      const progress = scrollDistance / Math.max(page.offsetHeight - window.innerHeight, 1);

      page.style.setProperty('--home-scroll-progress', progress.toFixed(4));

      if (!reduceMotion && !mobileViewport) {
        page.style.setProperty('--home-hero-shift', `${Math.min(scrollDistance * 0.07, 64)}px`);
        page.style.setProperty('--home-orb-shift', `${scrollDistance * -0.018}px`);
        page.style.setProperty('--home-orb-drift', `${Math.sin(progress * Math.PI * 2) * 44}px`);
      }
    };

    const requestScrollUpdate = () => {
      if (frameId === null) {
        frameId = window.requestAnimationFrame(updateScrollEffects);
      }
    };

    window.addEventListener('scroll', requestScrollUpdate, { passive: true });
    window.addEventListener('resize', requestScrollUpdate);
    updateScrollEffects();

    let observer;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      sections.forEach((section) => section.classList.add('is-motion-visible'));
    } else {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-motion-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '-8% 0px -16% 0px', threshold: 0.14 }
      );

      sections.forEach((section) => observer.observe(section));
    }

    return () => {
      window.removeEventListener('scroll', requestScrollUpdate);
      window.removeEventListener('resize', requestScrollUpdate);
      if (frameId !== null) window.cancelAnimationFrame(frameId);
      if (observer) observer.disconnect();
    };
  }, []);

  return (
    <main ref={pageRef} className="home-motion-page">
      <div className="home-scroll-progress" aria-hidden="true" />

      <div className="home-motion-atmosphere" aria-hidden="true">
        <span className="home-motion-orb home-motion-orb--pink" />
        <span className="home-motion-orb home-motion-orb--blue" />
      </div>

      <div className="home-motion-content">{children}</div>
    </main>
  );
};

export default HomeMotion;
